import type { WorkBook } from 'xlsx';

export const STUDENT_COLUMNS = [
  'first_name',
  'last_name',
  'email',
  'dob',
  'class',
];
export const MAX_SPREADSHEET_BYTES = 10 * 1024 * 1024;

/** Both file formats feed the same student validation and enrollment workflow. */
export async function parseStudentSpreadsheet(
  data: ArrayBuffer,
  filename: string,
) {
  const extension = filename.split('.').pop()?.toLowerCase();
  if (!extension || !['csv', 'xlsx', 'xls'].includes(extension)) {
    throw new Error('Choose a CSV, XLSX, or XLS file.');
  }
  if (data.byteLength > MAX_SPREADSHEET_BYTES) {
    throw new Error('Choose a file smaller than 10 MB.');
  }
  // Lazy loading keeps the spreadsheet library out of the initial page bundle.
  const XLSX = await import('xlsx');
  let workbook: WorkBook;
  try {
    workbook = XLSX.read(data, {
      type: 'array',
      raw: true,
      cellDates: false,
      ...(extension === 'csv' ? { codepage: 65001 } : {}),
    });
  } catch {
    throw new Error(
      'Could not read this file. Save an unprotected CSV or Excel file and try again.',
    );
  }
  const sheetName = workbook.SheetNames[0];
  const sheet = workbook.Sheets[sheetName];
  if (!sheet) throw new Error('The file has no worksheets.');
  const grid = XLSX.utils.sheet_to_json<unknown[]>(sheet, {
    header: 1,
    raw: true,
    defval: '',
    blankrows: true,
  });
  const headers = (grid[0] || []).map((value) =>
    String(value).trim().toLowerCase(),
  );
  const missing = STUDENT_COLUMNS.filter((column) => !headers.includes(column));
  if (missing.length)
    throw new Error(`Missing columns in the first row: ${missing.join(', ')}.`);
  if (
    STUDENT_COLUMNS.some(
      (column) => headers.filter((header) => header === column).length !== 1,
    )
  ) {
    throw new Error('Each required column must appear exactly once.');
  }
  const origin = XLSX.utils.decode_range(sheet['!ref'] || 'A1').s;
  const date1904 = Boolean(workbook.Workbook?.WBProps?.date1904);
  const rows = grid
    .slice(1)
    .map((values, index) => {
      const row: Record<string, string> = {};
      STUDENT_COLUMNS.forEach((column) => {
        const columnIndex = headers.indexOf(column);
        const value = values[columnIndex] ?? '';
        const cell =
          sheet[
            XLSX.utils.encode_cell({
              r: origin.r + index + 1,
              c: origin.c + columnIndex,
            })
          ];
        // Do not trust a formula's cached value for account identity or birthday.
        if (cell?.f || cell?.t === 'e')
          throw new Error(
            `Row ${index + 2}: replace formulas or cell errors with values.`,
          );
        if (
          column === 'dob' &&
          typeof value === 'number' &&
          extension !== 'csv'
        ) {
          const date = XLSX.SSF.parse_date_code(value, { date1904 });
          if (!date || (!date1904 && Math.floor(value) === 60)) {
            throw new Error(`Row ${index + 2}: invalid Excel birthday.`);
          }
          // Read calendar components directly; local timezone conversion can shift a birthday.
          row[column] = `${String(date.y).padStart(4, '0')}-${String(
            date.m,
          ).padStart(2, '0')}-${String(date.d).padStart(2, '0')}`;
        } else {
          row[column] = String(value).trim();
        }
      });
      return row;
    })
    .filter((row) => Object.values(row).some(Boolean));
  return { rows, sheetName };
}
